<template>
    <form class="profile-form" v-on:submit.prevent="updateProfile" v-on:input="isAllow = true">
        <g-caption size="1">{{ $t('parts.profile', 'Profile') }}</g-caption>
        <div class="infobar">
            <g-caption size="4" weight="bold">ID: <span class="title-color">{{ profile.id }}-{{ profile.lead_id }}</span></g-caption>

            <fieldset>
                <legend>Level:</legend>
                <g-flex align="center" justify="start" gap="5">
                    <div class="level-icon" v-bind:class="(profile.account_level || '').toLowerCase()"></div>
                    <g-caption size="2">{{ profile.account_level }}</g-caption>
                </g-flex>
            </fieldset>

            <fieldset>
                <legend>Balance:</legend>
                <g-flex align="center">
                    <g-symbol-currency name="usdt" width="18" height="18"/>
                    {{ profile.balance }}
                </g-flex>
            </fieldset>

            <fieldset>
                <legend>Email confirmed:</legend>
                <div v-bind:class="phoneConfirmed">{{ $t(`base.${emailConfirmed.toLowerCase()}`, emailConfirmed) }}</div>
            </fieldset>

            <fieldset>
                <legend>Phone confirmed:</legend>
                <div v-bind:class="phoneConfirmed">{{ $t(`base.${phoneConfirmed.toLowerCase()}`, phoneConfirmed) }}</div>
            </fieldset>
            <!--
            <fieldset>
                <legend>Registration at:</legend>
                {{ profile.created_at }}
            </fieldset>
            -->
        </div>
        <g-field v-model="firstName"
                 v-bind:label="$t('profile.first_name')"
                 v-bind:error="errors.first_name"/>

        <g-field v-model="lastName"
                 v-bind:label="$t('profile.last_name')"
                 v-bind:error="errors.last_name"/>

        <g-field v-model="email"
                 type="email"
                 v-bind:label="$t('profile.email')"
                 v-bind:error="errors.email"/>

        <g-phone-input v-if="countries.state.length"
                       v-model="phone"
                       v-model:country="profile.country_code"
                       v-bind:countries="countries.state"
                       v-bind:label="$t('profile.phone')"
                       v-bind:error="errors.phone"/>

        <g-flex class="password" align="end" gap="5">
            <g-field v-model="profile.password" type="password" v-bind:label="$t('profile.password')" disabled/>
            <g-button type="button" v-on:click="editPassword" class="password-btn">
                <g-symbol name="edit" width="18" height="18"/>
            </g-button>
        </g-flex>


        <g-button class="save-btn"
                  type="submit"
                  v-bind:disabled="!isAllow">
            {{ $t('base.save', 'Save') }}
        </g-button>


    </form>
</template>

<script>

import { computed, ref, reactive, watch } from 'vue';
import {verifyField, PopUpManager as PopUp, i18n} from '@/Ship';
import { useProfileActions } from '@profile';

const {
    getCountryResource,
    getProfileResource,
} = useProfileActions();

export default {
    name: 'Info',

    setup: function () {
        const profile = getProfileResource();
        const countries = getCountryResource();

        const isAllow = ref(false);
        const isComplex = ref(false);
        const errors = reactive({
            first_name: null,
            last_name: null,
            email: null,
            phone: null,
        });

        const firstName = computed({
            get: () => profile.first_name,
            set: value => {
                profile.first_name = value;
                errors.first_name = verifyField(value, ['require', 'minLength:2']);
                return true;
            }
        });

        const lastName = computed({
            get: () => profile.last_name,
            set: value => {
                profile.last_name = value;
                errors.last_name = verifyField(value, ['require', 'minLength:2']);
                return true;
            }
        });

        const email = computed({
            get: () => profile.email,
            set: value => {
                profile.email = value;
                errors.email = verifyField(value, ['require', 'email']);
                return true;
            }
        });

        const phone = computed({
            get: () => profile.phone,
            set: value => {
                profile.phone = value;
                errors.phone = verifyField(value, ['require', 'phone']);
                return true;
            }
        });

        const emailConfirmed = computed(() => {
            return profile.email_confirmed.toLowerCase();
        });

        const phoneConfirmed = computed(() => {
            return profile.phone_confirmed.toLowerCase();
        });

        const updateProfile = () => {
            if (profile.isValidationPassed()) {
                profile.save();
                profile.clearEntity();
            }
        };

        watch([errors, () => profile.password], () => {
            isAllow.value = true;
            for (const key in errors) {
                if (errors[key] !== null) {
                    isAllow.value = false;
                    break;
                }
            }
        });

        function editPassword() {
            PopUp.open(require('@profile/components/EditPasswordPopUp')).setCaption(i18n.global.t('profile.change_password'))
        }

        return {
            profile,
            countries,
            firstName,
            lastName,
            email,
            phone,
            errors,
            isAllow,
            isComplex,
            emailConfirmed,
            phoneConfirmed,
            updateProfile,
            editPassword,
        };
    }
};
</script>

<style lang="scss" scoped>
.profile-form {
    gap: 15px;
    display: flex;
    align-content: start;
    justify-content: end;
    flex-wrap: wrap;
    max-width: 360px;

    & > .g-caption {
        margin-top: -60px;
        color: var(--title-text-color, $title-text-color);
    }

    & > .g-field,
    & > .g-caption,
    & > .password-input {
        width: 100%;
    }
}

.save-btn {
    padding: 0 10px;
    min-width: 100px;
    color: white !important;
    background-color: var(--primary, $primary);

    &:hover {
        background-color: var(--primary-hover, $primary-hover);
    }
}

.password {
    width: 100%;

    & > .g-field {
        flex-grow: 1;
    }

    & > .password-btn {
        width: 40px;
        background-color: transparent;
        fill: var(--success-hover, $success-hover);
        border: 1px solid var(--success, $success);

        &:hover {
            fill: var(--title-text-color);
            background-color: var(--success, $success);
        }
    }
}

.infobar {
    padding: 20px;
    width: 100%;
    border-radius: var(--bar-rounding, $bar-rounding);
    border: 1px solid var(--separator-color, $separator-color);

    fieldset {
        margin: 15px 0;
        color: var(--title-text-color, $title-text-color);
        font-family: var(--font-title, $font-title);
        fill: var(--main-text-color, $main-text-color);
        font-size: 1.1rem;

        legend {
            font-size: 0.9rem;
            text-transform: uppercase;
            font-family: var(--font-primary, $font-primary);
            color: var(--main-text-color, $main-text-color);
        }
    }

    .g-caption {
        padding: 0 !important;
    }

    .yes {
        color: var(--success-hover, $success-hover);
    }

    .no {
        color: var(--danger-hover, $danger-hover);
    }

    .title-color {
        color: var(--title-text-color, $title-text-color);
    }
}

.level-icon {
    width: 26px;
    height: 26px;
    -webkit-mask: url('@assets/img/account-level.svg') no-repeat center;
    mask: url('@assets/img/account-level.svg') no-repeat center;

    //.amateur {
    background-color: #d8e6ef;
    //}

    &.professional {
        background-color: #ff914d;
    }

    &.ruby {
        background-color: #ec1b2e;
    }

    &.emerald {
        background-color: #05ff54;
    }

    &.dragon {
        background-color: #ad48fd;
    }

    &.halal {
        background-color: #0097b2;
    }

    &.standard {
        background-color: #349df2;
    }
}
</style>
